import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiUser, FiArrowRight, FiClock } from 'react-icons/fi';
import { optimizedAPI } from '../lib/optimizedApi';

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await optimizedAPI.get('/blogs', {
        params: { per_page: 3, page: 1 }
      });
      
      const result = response.data;
      if (result.success && result.data) {
        setBlogs(result.data.posts || []);
      } else {
        setBlogs([]);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getTitle = (blog) => {
    if (!blog.title) return '';
    if (typeof blog.title === 'string') return blog.title;
    if (blog.title.rendered) return blog.title.rendered;
    return '';
  };

  const getExcerpt = (blog) => {
    if (!blog.excerpt) return '';
    if (typeof blog.excerpt === 'string') return blog.excerpt;
    if (blog.excerpt.rendered) return blog.excerpt.rendered;
    return '';
  };

  if (loading) {
    return (
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'serif' }}>
              Latest from Our Blog
            </h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Stay updated with the latest dance tips, trends, and insights from the Bailebelle community.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-gray-800 rounded-2xl overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-700"></div>
                <div className="p-6">
                  <div className="h-4 bg-gray-700 rounded mb-2"></div>
                  <div className="h-4 bg-gray-700 rounded w-3/4 mb-4"></div>
                  <div className="h-3 bg-gray-700 rounded mb-2"></div>
                  <div className="h-3 bg-gray-700 rounded w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
      <section className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'serif' }}>
            Latest from Our Blog
          </h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Stay updated with the latest dance tips, trends, and insights from the Bailebelle community.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.slice(0, 3).map((blog) => (
            <article
              key={blog.id}
              className="bg-gradient-to-br from-gray-800/60 to-gray-900/40 backdrop-blur-sm border border-gray-700/50 rounded-3xl overflow-hidden hover:from-gray-800/80 hover:to-gray-900/60 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-pink-500/20 group"
            >
              <div className="relative overflow-hidden">
                <img
                  src={
                    blog.featured_image ||
                    'https://images.unsplash.com/photo-1508700929628-666bc8bd84ea?w=600&h=400&fit=crop'
                  }
                  alt={stripHtml(getTitle(blog))}
                  className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              <div className="p-6">
                <div className="flex items-center space-x-4 text-sm text-gray-400 mb-4">
                  <div className="flex items-center space-x-1">
                    <FiCalendar className="w-4 h-4" />
                    <span>{formatDate(blog.published_at || blog.created_at || blog.date)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FiUser className="w-4 h-4" />
                    <span>{blog.author?.name || blog._embedded?.author?.[0]?.name || 'Bailebelle Team'}</span>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-white mb-3 line-clamp-2 group-hover:text-pink-400 transition-colors">
                  {stripHtml(getTitle(blog))}
                </h3>

                <p className="text-gray-400 text-sm leading-relaxed mb-4 line-clamp-3">
                  {stripHtml(getExcerpt(blog))}
                </p>

                <Link
                  to={`/blog/${blog.slug || blog.id}`}
                  className="inline-flex items-center text-pink-500 hover:text-pink-400 font-semibold text-sm transition-colors group/link"
                >
                  Read More
                  <FiArrowRight className="ml-2 w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/blog"
            className="inline-flex items-center px-8 py-4 bg-pink-500 text-black font-bold rounded-xl hover:bg-pink-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-pink-500/30"
          >
            View All Posts
            <FiArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
